import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

export type PackageStatus = "Draft" | "Exported" | "Imported" | "Failed";
export type ImportAction = "create" | "update" | "unchanged" | "unsupported" | "invalid";

export interface PackageManifestItem {
  type: string;
  key: string;
  version?: number | null;
  name: string;
  nameAr?: string | null;
  sha256: string;
  dependsOn: string[];
}

export interface PackageManifest {
  format: string;
  key: string;
  name: string;
  nameAr?: string | null;
  version: string;
  publisher: string;
  description?: string | null;
  createdAt: string;
  createdBy: string;
  platformVersion: string;
  dependencies: string[];
  items: PackageManifestItem[];
  template?: string | null;
}

export interface SolutionPackage {
  id: string;
  key: string;
  name: string;
  nameAr?: string | null;
  version: string;
  description?: string | null;
  publisher: string;
  status: PackageStatus;
  sha256: string;
  sizeBytes: number;
  itemCount: number;
  storageKey?: string | null;
  exportedByName?: string | null;
  exportedAt?: string | null;
  importedByName?: string | null;
  importedAt?: string | null;
  importChangeSetId?: string | null;
  isTemplate: boolean;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
  manifest: PackageManifest;
}

export interface GalleryEntry {
  key: string;
  name: string;
  nameAr?: string | null;
  description: string;
  descriptionAr?: string | null;
  kind: "builtin" | "package";
  itemCount: number;
  itemsByType: Record<string, number>;
  packageId?: string | null;
  version?: string | null;
}

export interface CatalogueItem { type: string; key: string; name?: string | null; version?: number | null; id: string }
export type Catalogue = Record<string, CatalogueItem[]>;

export interface ExportPreviewItem { type: string; key: string; name: string; version?: number | null; selected: boolean; requiredBy?: string | null }
export interface ExportPreview { items: ExportPreviewItem[]; warnings: string[] }

export interface ExportInput {
  key: string;
  name: string;
  nameAr?: string | null;
  version: string;
  description?: string | null;
  publisher?: string | null;
  items: Array<{ type: string; key: string }>;
  resolveDependencies: boolean;
  isTemplate: boolean;
}

export interface ImportPlanItem {
  type: string;
  key: string;
  name: string;
  packageVersion?: number | null;
  action: ImportAction;
  currentVersion?: number | null;
  diffSummary?: string | null;
  checksumOk: boolean;
  issues: string[];
}

export interface ImportPlan {
  manifest: PackageManifest;
  computedSha256: string;
  checksumOk: boolean;
  errors: string[];
  warnings: string[];
  items: ImportPlanItem[];
  missingDependencies: string[];
  canImport: boolean;
}

export interface ImportResult {
  packageId: string;
  changeSetId?: string | null;
  items: Array<{ type: string; key: string; outcome: "staged" | "unchanged" | "skipped" | "failed" | string; error?: string | null }>;
}

/** Definition types in the order packages import them (dependencies first). */
export const TYPE_ORDER = ["role", "setting", "flag", "llmProvider", "identityProvider", "connector", "fee", "template", "rule", "form", "agent", "automation", "workflow", "service"];

export function usePackages() {
  const api = useApi();
  return useQuery({ queryKey: ["packages"], queryFn: () => api.get<SolutionPackage[]>("/api/platform/packages"), refetchInterval: 30_000 });
}

export function usePackage(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["package", id], queryFn: () => api.get<SolutionPackage>(`/api/platform/packages/${id}`), enabled: !!id });
}

export function usePackagePlan(id: string | null, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["package", id, "plan"], queryFn: () => api.get<ImportPlan>(`/api/platform/packages/${id}/plan`), enabled: !!id && enabled, staleTime: 15_000 });
}

export function useGallery() {
  const api = useApi();
  return useQuery({ queryKey: ["packages", "gallery"], queryFn: () => api.get<GalleryEntry[]>("/api/platform/packages/gallery"), staleTime: 60_000 });
}

export function useCatalogue(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["packages", "catalogue"], queryFn: () => api.get<Catalogue>("/api/platform/packages/catalogue"), enabled, staleTime: 30_000 });
}

export function usePackageActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["packages"] });
    void qc.invalidateQueries({ queryKey: ["package"] });
    void qc.invalidateQueries({ queryKey: ["change-sets"] });
    void qc.invalidateQueries({ queryKey: ["platform-overview"] });
  };
  return {
    /** What an export would carry; stores nothing, so its failure is not headed "Not saved". */
    preview: useSave({ mutationFn: (input: { items: Array<{ type: string; key: string }>; resolveDependencies: boolean }) => api.post<ExportPreview>("/api/platform/packages/preview", input), failed: t("admin:common.error") }),
    exportPackage: useSave({ mutationFn: (input: ExportInput) => api.post<SolutionPackage>("/api/platform/packages/export", input), onSuccess: invalidate }),
    exportTemplate: useSave({ mutationFn: (input: { templateKey: string; version?: string | null }) => api.post<SolutionPackage>(`/api/platform/packages/export/template/${input.templateKey}`, { version: input.version ?? null }), onSuccess: invalidate }),
    upload: useSave({
      mutationFn: (file: File) => {
        const form = new FormData();
        form.append("file", file, file.name);
        return api.upload<{ package: SolutionPackage; plan: ImportPlan }>("/api/platform/packages/upload", form);
      },
      onSuccess: invalidate,
    }),
    uploadJson: useSave({
      mutationFn: (json: string) => api.post<{ package: SolutionPackage; plan: ImportPlan }>("/api/platform/packages/upload", JSON.parse(json) as unknown),
      onSuccess: invalidate,
    }),
    importPackage: useSave({ mutationFn: (input: { id: string; title?: string | null; keys?: string[] | null; submit?: boolean }) => api.post<ImportResult>(`/api/platform/packages/${input.id}/import`, { title: input.title ?? null, keys: input.keys ?? null, submit: input.submit ?? false }), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/packages/${id}`), onSuccess: invalidate }),
    download: (p: SolutionPackage) => api.download(`/api/platform/packages/${p.id}/download`, `${p.key}-${p.version}.ncp-package.json`),
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
